import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldCheck, FileWarning, AlertTriangle } from "lucide-react";

export default function Compliance() {
  const { data, isLoading } = useQuery({ queryKey: ["compliance"], queryFn: async () => (await api.get("/dashboard/compliance")).data });
  const { data: summary } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard/summary")).data });

  if (isLoading || !data) return <Loading />;
  const items = data.expiring_documents || [];
  const d = summary?.documents || { expiring: 0, expired: 0 };

  return (
    <div>
      <PageHeader overline="Monitoring" title="Compliance Dashboard" icon={ShieldCheck}
        subtitle="Documents approaching or past expiry across the workforce." />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 border-slate-200 shadow-none">
          <FileWarning className="h-6 w-6 text-amber-500 mb-2" />
          <p className="font-serif-display text-3xl text-navy">{d.expiring}</p>
          <p className="text-sm text-slate-500">Expiring within 30 days</p>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <AlertTriangle className="h-6 w-6 text-red-500 mb-2" />
          <p className="font-serif-display text-3xl text-navy">{d.expired}</p>
          <p className="text-sm text-slate-500">Already expired</p>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <ShieldCheck className="h-6 w-6 text-emerald-500 mb-2" />
          <p className="font-serif-display text-3xl text-navy">{Math.max((d.total || 0) - d.expiring - d.expired, 0)}</p>
          <p className="text-sm text-slate-500">Valid documents</p>
        </Card>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Fully compliant" hint="No documents are expiring or expired right now." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Employee</TableHead>
              <TableHead className="overline text-slate-500">Emp ID</TableHead>
              <TableHead className="overline text-slate-500">Document</TableHead>
              <TableHead className="overline text-slate-500">Expiry Date</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} className="table-row-i" data-testid={`compliance-row-${it.id}`}>
                  <TableCell className="font-medium text-navy">{it.employee_name}</TableCell>
                  <TableCell className="font-mono-i text-sm text-teal">{it.employee_code}</TableCell>
                  <TableCell className="text-slate-600">{it.document_type}</TableCell>
                  <TableCell className="text-slate-600">{it.expiry_date}</TableCell>
                  <TableCell><StatusBadge status={it.expiry_status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
